import { DealInformation } from '@luna-protocol/core/src/types';
import React, { useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { ProgramProps } from './components/ProgramSelect/ProgramSelect';
import { PostSerialResponse } from './queries/useGetSerialNumber';
import { UserDetails } from './types';
import { OEMType as EOEMType } from '@luna-protocol/core/src/types.ts';

type AppContextProviderProps = {
  children: React.ReactNode;
};

export type ApplicationSummaryDetails = {
  item_name: string;
  serial_numbers: [
    {
      serial_number_type: string;
      serial_number: string;
      product_price: number;
    },
  ];
  model: string;
  color: string;
  approved_amount?: number;
};

export type TotalsProps = {
  base_accessories?: number;
  additions?: number;
  backend_products?: number;
  deductions?: number;
  approved_amount?: number;
  base_product_total?: number;
  total_requested?: number;
  isFrontendLTVExceed?: boolean;
  isBackendLTVExceed?: boolean;
};

type ContextDefaults =
  | {
      userDetails?: UserDetails;
      setUserDetails: React.Dispatch<React.SetStateAction<UserDetails | undefined>>;
      prequalAmount?: string;
      setPrequalAmount: React.Dispatch<React.SetStateAction<string | undefined>>;
      deal?: DealInformation;
      setDeal: React.Dispatch<React.SetStateAction<DealInformation | undefined>>;
      totals?: TotalsProps;
      setTotals: React.Dispatch<React.SetStateAction<TotalsProps>>;
      applicationSummaryDetails: ApplicationSummaryDetails | undefined;
      setApplicationSummaryDetails: React.Dispatch<React.SetStateAction<ApplicationSummaryDetails | undefined>>;
      floorplanDetails: Map<string, PostSerialResponse>;
      setFloorplanDetails: React.Dispatch<React.SetStateAction<Map<string, PostSerialResponse>>>;
      loanApplicationID?: string;
      customerName?: string;
      setCustomerName: React.Dispatch<React.SetStateAction<string | undefined>>;
      setLoanApplicationID: React.Dispatch<React.SetStateAction<string>>;
      selectedProgram: ProgramProps | undefined;
      setSelectedProgram: React.Dispatch<React.SetStateAction<ProgramProps | undefined>>;
      OEMType?: EOEMType;
      setOEMType: React.Dispatch<React.SetStateAction<EOEMType>>;
    }
  | Record<string, never>;

export const AppContext = React.createContext({} as ContextDefaults);

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
  const [userDetails, setUserDetails] = useSessionStorage<UserDetails | undefined>('userDetails', undefined);
  const [prequalAmount, setPrequalAmount] = useSessionStorage<string | undefined>('prequalAmount', undefined);
  const [deal, setDeal] = useState<DealInformation | undefined>();
  const [totals, setTotals] = useSessionStorage<TotalsProps>('totals', {});
  const [applicationSummaryDetails, setApplicationSummaryDetails] = useSessionStorage<
    ApplicationSummaryDetails | undefined
  >('applicationSummaryDetails', undefined);
  const [floorplanDetails, setFloorplanDetails] = useState<Map<string, PostSerialResponse>>(new Map());
  const [loanApplicationID, setLoanApplicationID] = useSessionStorage<string>('loanApplication_id', '');
  const [customerName, setCustomerName] = useState<string | undefined>('');
  const [selectedProgram, setSelectedProgram] = useState<ProgramProps | undefined>(undefined);
  const [OEMType, setOEMType] = useSessionStorage<EOEMType>('OEMType', EOEMType.Brunswick);

  return (
    <AppContext.Provider
      value={{
        userDetails,
        setUserDetails,
        prequalAmount,
        setPrequalAmount,
        deal,
        setDeal,
        totals,
        setTotals,
        applicationSummaryDetails,
        setApplicationSummaryDetails,
        floorplanDetails,
        setFloorplanDetails,
        loanApplicationID,
        setLoanApplicationID,
        customerName,
        setCustomerName,
        selectedProgram,
        setSelectedProgram,
        OEMType,
        setOEMType,
      }}>
      {children}
    </AppContext.Provider>
  );
};
